import React, { useRef, useState } from 'react';
import { Dialog } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { Button } from '../../components/Button';
import Input from '../../components/Input';
import InputMask from '../../components/InputMask';
import { Checkbox } from '../../components/Checkbox';
import logoImg from '../../assets/CEDAPP-CRM.png';
import { PendingSession, useAuth } from '../../hooks/auth';
import { useLoader } from '../../hooks/loading';
import {
  apiLaureano,
  apiPhato,
  authApi,
  CEDAPP_API_KEY,
} from '../../services/api';
import { VerificationCodeModal } from './Modal';
import { RegisterPhone } from './RegisterPhone';
import { createPhatoAccessContext } from './phato-access-context';
import {
  buildDoctorPhonePayload,
  getLaureanoPhoneEndpoint,
  getPhatoPhoneExistsEndpoint,
  getPhatoPhoneRegisterEndpoint,
  getPhatoPhoneResourceEndpoint,
  hasRegisteredPhone,
  interpretPhoneExistsStatus,
  interpretPhoneResourceStatus,
  isAdminLogin,
  LoginApiName,
  PhonePresence,
  PHATO_PHONE_UNAVAILABLE_MESSAGE,
  SUPPORT_PHONE_MESSAGE,
} from './login-flow';
import { Container, Content, Logo, Title } from './styles';

interface LoginObjectProps {
  login: string;
  password: string;
  isMedico: boolean;
}

const apiKeyHeaders = { 'x-api-key': CEDAPP_API_KEY };

export function SignIn() {
  const history = useHistory();
  const [loginObject, setLoginObject] = useState<LoginObjectProps>({
    isMedico: true,
    login: '',
    password: '',
  });
  const [verificationCode, setVerificationCode] = useState(false);
  const [registerPhone, setRegisterPhone] = useState(false);
  // Sessões autenticadas aguardando o OTP; só vão para o sessionStorage em enterApp.
  const pendingSessionsRef = useRef<PendingSession[]>([]);
  const { loadStorageData, requestSession, commitSessions } = useAuth();
  const { showLoader, hideLoader } = useLoader();

  function getSessionCredentials() {
    return {
      login: loginObject.login,
      password: loginObject.password,
      isMedico: loginObject.isMedico,
    };
  }

  async function authenticateApis(): Promise<LoginApiName[]> {
    const credentials = getSessionCredentials();
    const apiNames: LoginApiName[] = ['phato', 'laureano', 'laureanoSoul', 'hnsn'];
    const results = await Promise.allSettled(
      apiNames.map((apiName) => requestSession(apiName, credentials)),
    );

    pendingSessionsRef.current = results.reduce<PendingSession[]>(
      (sessions, result) => {
        if (result.status === 'fulfilled') sessions.push(result.value);
        return sessions;
      },
      [],
    );

    return pendingSessionsRef.current.map((session) => session.apiName);
  }

  async function enterApp() {
    const sessions = pendingSessionsRef.current;
    const hasPhato = sessions.some((session) => session.apiName === 'phato');

    await commitSessions(
      sessions,
      hasPhato ? createPhatoAccessContext(loginObject) : null,
    );
    pendingSessionsRef.current = [];
    await loadStorageData();
    history.push('/home');
  }

  function discardPendingSessions() {
    pendingSessionsRef.current = [];
  }

  function abortAuthenticatedSession() {
    setVerificationCode(false);
    setRegisterPhone(false);
    discardPendingSessions();
  }

  async function getPhatoPhoneStatus(): Promise<PhonePresence> {
    const normalizedLogin = loginObject.login.replace(/\D/g, '');

    try {
      const response = await apiPhato.get(
        getPhatoPhoneExistsEndpoint(loginObject.isMedico, normalizedLogin),
        {
          headers: apiKeyHeaders,
          validateStatus: () => true,
          silentErrorToast: true,
        },
      );

      return interpretPhoneExistsStatus(response.status);
    } catch (error) {
      return 'unavailable';
    }
  }

  async function getPhatoPhoneResourceStatus(): Promise<PhonePresence> {
    const normalizedLogin = loginObject.login.replace(/\D/g, '');

    try {
      const response = await apiPhato.get(
        getPhatoPhoneResourceEndpoint(loginObject.isMedico, normalizedLogin),
        {
          headers: apiKeyHeaders,
          validateStatus: () => true,
          silentErrorToast: true,
        },
      );

      return interpretPhoneResourceStatus(
        response.status,
        Boolean(response.data),
      );
    } catch (error) {
      return 'unavailable';
    }
  }

  async function getLaureanoMvPhoneStatus(): Promise<PhonePresence> {
    const normalizedLogin = loginObject.login.replace(/\D/g, '');

    try {
      const response = await apiLaureano.get(
        getLaureanoPhoneEndpoint(loginObject.isMedico, normalizedLogin),
        {
          headers: apiKeyHeaders,
          validateStatus: () => true,
          silentErrorToast: true,
        },
      );

      return interpretPhoneResourceStatus(
        response.status,
        Boolean(response.data),
      );
    } catch (error) {
      return 'unavailable';
    }
  }

  /**
   * Paciente: só conta telefone nos sistemas em que autenticou
   * (Patho e/ou Laureano MV) — mesmo critério da api-auth.
   */
  async function patientHasRegisteredPhone(successfulApis: LoginApiName[]) {
    const checks: Array<Promise<PhonePresence>> = [];

    if (successfulApis.includes('phato')) {
      checks.push(getPhatoPhoneResourceStatus());
    }

    if (successfulApis.includes('laureano')) {
      checks.push(getLaureanoMvPhoneStatus());
    }

    if (checks.length === 0) {
      return false;
    }

    const statuses = await Promise.all(checks);
    return hasRegisteredPhone(statuses);
  }

  async function handleGenerateCode() {
    try {
      await authApi.post(
        '/auth-tokens',
        {
          login: loginObject.login.replace(/\D/g, ''),
          password: loginObject.password,
          isDoctor: loginObject.isMedico,
        },
        { silentErrorToast: true },
      );
      setVerificationCode(true);
      return true;
    } catch (error) {
      alert('Não foi possível enviar o código de verificação.');
      return false;
    }
  }

  async function continueDoctorOtpFlow() {
    const phoneStatus = await getPhatoPhoneStatus();

    if (phoneStatus === 'exists') {
      const codeSent = await handleGenerateCode();
      if (!codeSent) discardPendingSessions();
      return;
    }

    if (phoneStatus === 'missing') {
      setRegisterPhone(true);
      return;
    }

    discardPendingSessions();
    alert(PHATO_PHONE_UNAVAILABLE_MESSAGE);
  }

  async function continuePatientOtpFlow(successfulApis: LoginApiName[]) {
    const hasPhone = await patientHasRegisteredPhone(successfulApis);

    if (!hasPhone) {
      discardPendingSessions();
      alert(SUPPORT_PHONE_MESSAGE);
      return;
    }

    const codeSent = await handleGenerateCode();
    if (!codeSent) {
      discardPendingSessions();
      alert(SUPPORT_PHONE_MESSAGE);
    }
  }

  async function handleSignIn() {
    if (!loginObject.login || !loginObject.password) {
      alert('Preencha os dados para fazer login!');
      return;
    }

    showLoader();
    try {
      const successfulApis = await authenticateApis();

      if (successfulApis.length === 0) {
        alert('Usuário ou senha inválidos');
        return;
      }

      if (isAdminLogin(loginObject.login)) {
        await enterApp();
        return;
      }

      // Médico sempre exige telefone no Phato + OTP (inclusive só HNSN).
      if (loginObject.isMedico) {
        await continueDoctorOtpFlow();
        return;
      }

      // Paciente só-HNSN (ou só Soul, sem Patho/MV) entra sem OTP.
      if (
        !successfulApis.includes('phato') &&
        !successfulApis.includes('laureano')
      ) {
        await enterApp();
        return;
      }

      await continuePatientOtpFlow(successfulApis);
    } finally {
      hideLoader();
    }
  }

  async function handleSendPhone(phone: string) {
    const onlyNumbers = phone.replace(/\D/g, '');
    const ddd = onlyNumbers.substring(0, 2);
    const number = onlyNumbers.substring(2);
    if (ddd.length !== 2 || number.length < 8) {
      alert('Telefone inválido. Verifique o DDD e o número.');
      return;
    }

    showLoader();
    try {
      await apiPhato.post(
        getPhatoPhoneRegisterEndpoint(true),
        buildDoctorPhonePayload({
          login: loginObject.login,
          password: loginObject.password,
          ddd,
          number,
        }),
        { headers: apiKeyHeaders },
      );

      setRegisterPhone(false);
      const codeSent = await handleGenerateCode();
      if (!codeSent) discardPendingSessions();
    } catch (error) {
      alert('Não foi possível cadastrar o telefone. Tente novamente.');
    } finally {
      hideLoader();
    }
  }

  async function handleVerifyCode(code: string) {
    if (!code) {
      alert('Código de verificação inválido');
      return;
    }

    showLoader();
    try {
      await authApi.post(
        '/validate-auth-tokens',
        {
          login: loginObject.login.replace(/\D/g, ''),
          token: code,
        },
        { silentErrorToast: true },
      );
      setVerificationCode(false);
      await enterApp();
    } catch (error) {
      alert('Código de verificação inválido');
    } finally {
      hideLoader();
    }
  }

  return (
    <Container>
      <Logo src={logoImg} alt="CEDAPP" />
      <div>
        <Title>Faça seu login</Title>
      </div>

      <Content>
        <div>
          <Checkbox
            onClick={(checked) =>
              setLoginObject((previous) => ({
                ...previous,
                isMedico: checked,
              }))
            }
            label="Acesso médico"
            checked={loginObject.isMedico}
          />
          <Checkbox
            onClick={(checked) =>
              setLoginObject((previous) => ({
                ...previous,
                isMedico: !checked,
              }))
            }
            label="Acesso paciente"
            checked={!loginObject.isMedico}
          />
        </div>

        {loginObject.isMedico ? (
          <>
            <Input
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  login: value,
                }));
              }}
              placeholder="CRM"
              value={loginObject.login}
            />
            <InputMask
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  password: value,
                }));
              }}
              value={loginObject.password}
              placeholder="CPF"
              mask="999.999.999-99"
            />
          </>
        ) : (
          <>
            <InputMask
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  login: value,
                }));
              }}
              value={loginObject.login}
              placeholder="CPF"
              mask="999.999.999-99"
            />
            <InputMask
              onChange={(event) => {
                const { value } = event.target;
                setLoginObject((previous) => ({
                  ...previous,
                  password: value,
                }));
              }}
              value={loginObject.password}
              placeholder="Data de nascimento"
              mask="99/99/9999"
            />
          </>
        )}

        <Dialog
          open={verificationCode}
          onClose={abortAuthenticatedSession}
          PaperProps={{ style: { background: 'transparent' } }}
        >
          <VerificationCodeModal
            handleCloseModal={abortAuthenticatedSession}
            handleSendVerificationCode={handleVerifyCode}
          />
        </Dialog>
        <Dialog
          open={registerPhone}
          onClose={abortAuthenticatedSession}
          PaperProps={{ style: { background: 'transparent' } }}
        >
          <RegisterPhone
            handleCloseModal={abortAuthenticatedSession}
            handleSendPhone={handleSendPhone}
          />
        </Dialog>

        <Button onClick={handleSignIn}>Entrar</Button>
      </Content>
    </Container>
  );
}
